/**
 * Reed.ai Design System — TextInput
 *
 * Variants: stacked (default) — label above the field ("Default form input —
 *           label above. Use for all platform forms.")
 *           floating — legacy floating-label render, retained only for the
 *           account sign-up exception while it migrates.
 * States:   Default | Focus | Filled | Error | Disabled
 * Options:  label, description, optional tag, label info slot, trailing icon,
 *           helper text, character counter
 * Sizes:    Desktop (56px field) | Mobile (52px field)
 *
 * Source: Figma "Reed.ai - Global components" — A / Form fields 🟢 updated
 * Nodes: Form Field / Desktop 9616:2123 · Form Field / Mobile 9617:2103
 *        (building blocks: Form Field Label 9005:2071, Field input 9601:281)
 */

import React, { useId, useState } from 'react';
import { cn } from '../../utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TextInputState   = 'default' | 'active' | 'populated' | 'error' | 'inactive';
export type TextInputSize    = 'desktop' | 'mobile';
export type TextInputVariant = 'stacked' | 'floating';

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Field label. Stacked: rendered above the field. Provide aria-label when omitted. */
  label?: string;
  /** 'stacked' (Figma default — label above) or legacy 'floating' (sign-up only) */
  variant?: TextInputVariant;
  /** Description line under the label (stacked only) */
  description?: string;
  /** Shown as "(optional)" next to label */
  optional?: boolean;
  /** Slot after the label row, e.g. an info icon with Tooltip (stacked only) */
  labelInfo?: React.ReactNode;
  /** Trailing icon element (24×24) */
  icon?: React.ReactNode;
  /** Helper text shown below the field */
  helpText?: string;
  /** Error message — switches field to error state */
  errorText?: string;
  /** Current character count for the counter display */
  charCount?: number;
  /** Maximum character count for the counter display */
  maxCount?: number;
  /** Desktop = 56px field height, Mobile = 52px */
  size?: TextInputSize;
  /** Override the visual state (useful for Storybook / demos) */
  stateOverride?: TextInputState;
  className?: string;
  inputClassName?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Reed.ai TextInput
 *
 * @example
 * // Stacked (default) — label above, real placeholder
 * <TextInput label="Email address" type="email" placeholder="you@company.com" />
 *
 * // With all options
 * <TextInput
 *   label="Bio"
 *   description="Shown on your public profile"
 *   optional
 *   helpText="Tell candidates about yourself"
 *   maxCount={200}
 * />
 *
 * // Error state
 * <TextInput label="Email" errorText="Please enter a valid email address" />
 *
 * // Legacy floating label (account sign-up only)
 * <TextInput variant="floating" label="Company" />
 */
export function TextInput({
  label,
  variant = 'stacked',
  description,
  optional = false,
  labelInfo,
  icon,
  helpText,
  errorText,
  charCount,
  maxCount,
  size = 'desktop',
  stateOverride,
  className,
  inputClassName,
  id: idProp,
  value,
  defaultValue,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  disabled,
  ...rest
}: TextInputProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  const [focused, setFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');

  const controlledValue = value !== undefined ? value : internalValue;
  const hasValue = String(controlledValue).length > 0;
  const hasError = !!errorText;
  const isInactive = disabled;

  // Derive visual state
  const state: TextInputState =
    stateOverride ??
    (isInactive ? 'inactive' :
     hasError    ? 'error' :
     focused     ? 'active' :
     hasValue    ? 'populated' :
                   'default');

  const containerHeight = size === 'mobile'
    ? 'h-[calc(var(--space-10)+var(--space-3))]'
    : 'h-[calc(var(--space-10)+var(--space-4))]';

  const currentCount = charCount ?? (value !== undefined
    ? String(value).length
    : String(internalValue).length);

  const bottomText = errorText ?? helpText;
  const showMetaRow = !!bottomText || !!maxCount;

  const sharedInputHandlers = {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) setInternalValue(e.target.value);
      onChange?.(e);
    },
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    },
  };

  // ── Stacked (Figma default: label above, real placeholder) ──────────────────
  if (variant === 'stacked') {
    const fieldBorder =
      state === 'error'    ? 'border-[1.5px] border-border-error' :
      state === 'active'   ? 'border-[1.5px] border-text-primary' :
                             'border border-border-input';
    const fieldBg = state === 'inactive' ? 'bg-surface-tertiary' : 'bg-surface-primary';

    return (
      <div className={cn('w-full flex flex-col gap-[6px]', className)}>
        {/* Label block */}
        {(label || description) && (
          <div className="flex flex-col gap-1 items-start justify-center">
            {label && (
              <div className="flex gap-1 items-center">
                <label
                  className="font-body text-body-sm font-medium text-text-primary"
                  htmlFor={id}
                >
                  {label}
                </label>
                {optional && (
                  <span className="font-body text-body-sm text-text-secondary">(optional)</span>
                )}
                {labelInfo && (
                  <span className="shrink-0 size-4 flex items-center justify-center">{labelInfo}</span>
                )}
              </div>
            )}
            {description && (
              <p className="font-body text-body-sm text-neutral-90">{description}</p>
            )}
          </div>
        )}

        {/* Field */}
        <div
          className={cn(
            'flex items-center gap-2 w-full rounded-s px-4',
            containerHeight,
            fieldBorder,
            fieldBg,
          )}
        >
          <input
            {...rest}
            className={cn(
              'w-full h-full bg-transparent outline-none border-none p-0',
              'font-body text-body-md font-normal',
              state === 'inactive' ? 'text-text-secondary' : 'text-text-primary',
              'placeholder:font-body placeholder:text-body-md placeholder:font-normal placeholder:text-text-secondary',
              isInactive && 'cursor-not-allowed',
              inputClassName,
            )}
            disabled={!!isInactive}
            id={id}
            placeholder={placeholder}
            value={controlledValue}
            {...sharedInputHandlers}
          />
          {icon && (
            <span
              className={cn(
                'shrink-0 size-6 flex items-center justify-center',
                state === 'inactive' ? 'text-text-secondary' : 'text-neutral-70',
              )}
            >
              {icon}
            </span>
          )}
        </div>

        {/* Helper / error text + counter */}
        {showMetaRow && (
          <div className="flex items-start justify-between gap-4">
            {bottomText ? (
              <p
                className={cn(
                  'font-body text-body-xs font-medium',
                  hasError ? 'text-text-error' : 'text-neutral-70',
                )}
              >
                {bottomText}
              </p>
            ) : (
              <span aria-hidden="true" />
            )}
            {maxCount && (
              <span className="shrink-0 font-body text-body-xs font-medium text-neutral-70 text-right">
                {currentCount}/{maxCount}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Floating (legacy — account sign-up only) ────────────────────────────────
  const isFloating = state === 'active' || state === 'populated' || state === 'error';

  const borderColor =
    state === 'error'    ? 'border-system-error' :
    state === 'active'   ? 'border-text-primary' :
    state === 'inactive' ? 'border-border-secondary' :
                           'border-border-tertiary';

  const borderWidth = state === 'active' || state === 'error' ? 'border-[1.5px]' : 'border';

  const floatingLabelColor =
    state === 'error'    ? 'text-system-error' :
    state === 'active'   ? 'text-text-primary' :
                           'text-neutral-70';

  const fieldTextColor =
    state === 'inactive' ? 'text-text-secondary' :
    state === 'active'   ? 'text-neutral-70' :
                           'text-neutral-90';

  const inlineLabelColor = state === 'inactive' ? 'text-text-secondary' : 'text-neutral-70';
  const iconColor = state === 'inactive' ? 'text-text-secondary' : 'text-neutral-70';

  return (
    <div className={cn('relative w-full flex flex-col gap-2', className)}>
      <div className="relative">
        {/* Floating label */}
        {isFloating && label && (
          <label
            className={cn(
              'absolute left-3 -top-2 z-10',
              'bg-white px-1',
              'font-body text-body-xs font-medium whitespace-nowrap',
              floatingLabelColor,
            )}
            htmlFor={id}
          >
            {label}
            {optional && <span className={cn('ml-1', state === 'active' ? 'text-text-primary' : floatingLabelColor)}>(optional)</span>}
          </label>
        )}

        {/* Input wrapper */}
        <div
          className={cn(
            'flex items-center gap-2',
            'bg-white rounded-s',
            borderWidth,
            borderColor,
            containerHeight,
            'px-4',
          )}
        >
          {/* Input */}
          <div className="relative flex-1 min-w-0 h-full flex items-center">
            {/* Placeholder label (default / inactive state — shown inside as label text) */}
            {!isFloating && label && (
              <label
                className={cn(
                  'absolute inset-0 flex items-center font-body text-body-md cursor-text pointer-events-none',
                  inlineLabelColor,
                )}
                htmlFor={id}
              >
                {label}
                {optional && <span className="ml-1">(optional)</span>}
              </label>
            )}

            <input
              {...rest}
              className={cn(
                'w-full h-full bg-transparent outline-none border-none p-0',
                'font-body text-body-md font-normal',
                hasValue || focused || isFloating ? fieldTextColor : 'text-transparent',
                'placeholder:font-body placeholder:text-body-md placeholder:font-normal placeholder:text-neutral-70',
                isInactive && 'cursor-not-allowed',
                inputClassName,
              )}
              disabled={!!isInactive}
              id={id}
              placeholder={placeholder}
              value={controlledValue}
              {...sharedInputHandlers}
            />
          </div>

          {/* Trailing icon */}
          {icon && (
            <span className={cn('shrink-0 size-6 flex items-center justify-center', iconColor)}>
              {icon}
            </span>
          )}
        </div>
      </div>

      {/* Help text / error text + counter */}
      {showMetaRow && (
        <div className="flex items-start justify-between gap-4 px-4">
          {bottomText ? (
            <p
              className={cn(
                'font-body text-body-xs font-medium',
                hasError ? 'text-system-error' : 'text-neutral-70',
              )}
            >
              {bottomText}
            </p>
          ) : (
            <span aria-hidden="true" />
          )}
          {maxCount && (
            <span className="shrink-0 font-body text-body-xs font-medium text-neutral-70 text-right">
              {currentCount}/{maxCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default TextInput;
